import { CALIBRATION_TYPE, CalibrationType } from '../../config/calibration';
import React, { useEffect, useState } from 'react';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { buildPrefill, PrefillContext } from './prefill';
import { formBuilderApi } from './api';
import { FormSchema } from './types';
import FormRenderer from './FormRenderer';
import { secondaryButton, Skeleton } from './ui';
import { TourButton } from './tour/TourButton';
import { ROUTES } from '../../config/links';

export default function FormRuntimePage() {
  const { formId = '' } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as { prefillContext?: PrefillContext; calibrationType?: CalibrationType } | null;
  const prefillContext = routeState?.prefillContext;
  const calibrationType = routeState?.calibrationType;
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    formBuilderApi.runtime(formId)
      .then((result) => { if (!cancelled) setSchema(result); })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Unable to load this form.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [formId]);

  if (loading) {
    return (
      <div className="min-h-full bg-slate-50" aria-busy="true" aria-label="Loading form">
        <div className="mx-auto max-w-5xl">
          <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="mb-5 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <Skeleton className="h-6 w-64 max-w-full" />
            <Skeleton className="mt-2 h-3 w-96 max-w-full" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-6 flex gap-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-28" />)}</div>
            <div className="grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-1.5"><Skeleton className="h-3 w-24" /><Skeleton className="h-10 w-full" /></div>
              ))}
            </div>
            <div className="mt-6 flex justify-end"><Skeleton className="h-10 w-28" /></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div><h1 className="text-sm font-semibold text-red-900">Unable to open form</h1><p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-red-700">{String(error || 'Published form not found')}</p></div>
          </div>
          <button type="button" className={`${secondaryButton} mt-4`} onClick={() => navigate(ROUTES.FORM_BUILDER_LIVE)}><ArrowLeft className="h-3.5 w-3.5" /> Back to form builder</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto max-w-5xl">
        <div data-tour="runtime-header" className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 text-right">
            <div className="text-[10px] font-medium text-emerald-600">Published runtime</div>
            {calibrationType === CALIBRATION_TYPE.RECALIBRATION && <div className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Re-calibration</div>}
            <TourButton id="runtime" />
          </div>
        </div>
        <div className="mb-5 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">{schema.title}</h1>
          {schema.description && <p className="mt-1 text-xs leading-5 text-slate-500">{schema.description}</p>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <FormRenderer schema={schema} runtimeFormId={formId} value={buildPrefill(schema, prefillContext)} dynamicSeed={prefillContext?.dynamicSeed as Record<string, unknown> | undefined} calibrationType={calibrationType} />
        </div>
      </div>
    </div>
  );
}
